import React, { useEffect, useState } from "react";
import logger from "utils/logger";

const MEDIA_ELEMENT_STATE_EVENTS: (keyof HTMLMediaElementEventMap)[] = [
  "loadeddata",
  "loadedmetadata",
  "load",
  "emptied",
  "abort",
  "error",
  "ended",
  "playing",
] as const;

const describeReadyState = (state: number) => {
  switch (state) {
    case HTMLMediaElement.HAVE_NOTHING:
      return "HAVE_NOTHING";
    case HTMLMediaElement.HAVE_METADATA:
      return "HAVE_METADATA";
    case HTMLMediaElement.HAVE_CURRENT_DATA:
      return "HAVE_CURRENT_DATA";
    case HTMLMediaElement.HAVE_FUTURE_DATA:
      return "HAVE_FUTURE_DATA";
    case HTMLMediaElement.HAVE_ENOUGH_DATA:
      return "HAVE_ENOUGH_DATA";
    default:
      return state.toString();
  }
};

const useMediaElementReadyState = (
  videoRef: React.RefObject<HTMLMediaElement | null>
) => {
  const [readyState, setReadyState] = useState(
    videoRef.current?.readyState || HTMLMediaElement.HAVE_NOTHING
  );

  useEffect(() => {
    const video = videoRef.current;
    if (!video) {
      return;
    }

    const handleMediaEvent = (event: Event) => {
      logger.log(`HTMLMediaElement: event '${event.type}' happen`);
      if (event.target instanceof HTMLMediaElement) {
        const eventTarget = event.target;
        setReadyState((oldState) => {
          if (oldState !== eventTarget.readyState) {
            logger.log(
              `HTMLMediaElement: ready state changed from ${describeReadyState(oldState)} to ${describeReadyState(eventTarget.readyState)}`
            );
          }

          return eventTarget.readyState;
        });
      }
    };

    MEDIA_ELEMENT_STATE_EVENTS.forEach((eventName) => {
      video.addEventListener(eventName, handleMediaEvent);
    });

    return () => {
      MEDIA_ELEMENT_STATE_EVENTS.forEach((eventName) => {
        video.removeEventListener(eventName, handleMediaEvent);
      });
    };
  }, [videoRef]);

  return { readyState };
};

export default useMediaElementReadyState;
